/**
 * The sanctioned tone ramps (docs/design-guidelines.md §2 "Status palette") in
 * one table. Every status pill, score pill, categorical tag, tone dot and icon
 * tile in the app names a hue here instead of restating
 * `border-X-200 bg-X-50/60 text-X-700` at the call site. Re-tone a state once,
 * here, and every surface follows.
 *
 * Class strings are spelled out in full — never built by interpolation — so
 * Tailwind's source scan can see them.
 *
 * This table is presentation only; it does not decide *which* tone a thing
 * gets. Those maps live with their meaning:
 *   - semantic status names → `components/ui/status-pill.jsx` (STATUS_TONES)
 *   - roleplay module colors → `lib/role-play-theme.js`
 * Do not add a hue here for a one-off box; add it when a *set* of things needs
 * to be told apart at a glance.
 */

// `solid` is the hue's -500: the tone dot on a pill and the fill of a progress
// bar are the same colour, named for each job so a call site reads right.
// `inverse` is the hue as text on the espresso surface (`bg-surface-inverse`),
// lifted a step so it holds its contrast there (neutral's is the dark
// header's secondary text, about 6:1). Only the hues a dark surface uses carry
// one; read it with `getInverseText`, which falls back to neutral.
function ramp({ border, bg, text, solid, tile, icon, inverse }) {
  return {
    border,
    bg,
    text,
    dot: solid,
    fill: solid,
    tile,
    icon,
    inverse,
    pill: `${border} ${bg} ${text}`,
  };
}

export const TONES = {
  // Neutral is the one ramp without the /60 wash — a translucent gray-50 on
  // white is invisible, so it uses the flat tint.
  gray: ramp({
    border: "border-gray-200",
    bg: "bg-gray-50",
    text: "text-gray-600",
    solid: "bg-gray-400",
    tile: "bg-gray-50 text-gray-600",
    icon: "text-gray-600",
    inverse: "text-stone-400",
  }),
  // The coral accent as a tone, for a set of things that are "the brand"
  // rather than a status (Guide's cue types). Coral stays off body copy and
  // wide backgrounds (§2); the ramp is its small-print uses.
  brand: ramp({
    border: "border-brand-200",
    bg: "bg-brand-50/60",
    text: "text-brand-700",
    solid: "bg-brand-500",
    tile: "bg-brand-50 text-brand-600",
    icon: "text-brand-600",
    inverse: "text-brand-on-inverse",
  }),
  action: ramp({
    border: "border-action-200",
    bg: "bg-action-50/60",
    text: "text-action-700",
    solid: "bg-action-500",
    tile: "bg-action-50 text-action-600",
    icon: "text-action-600",
  }),
  premium: ramp({
    border: "border-premium-200",
    bg: "bg-premium-50/60",
    text: "text-premium-700",
    solid: "bg-premium-500",
    tile: "bg-premium-50 text-premium-600",
    icon: "text-premium-600",
  }),
  emerald: ramp({
    border: "border-emerald-200",
    bg: "bg-emerald-50/60",
    text: "text-emerald-700",
    solid: "bg-emerald-500",
    tile: "bg-emerald-50 text-emerald-600",
    icon: "text-emerald-600",
  }),
  warning: ramp({
    border: "border-warning-200",
    bg: "bg-warning-50/60",
    text: "text-warning-700",
    solid: "bg-warning-500",
    tile: "bg-warning-50 text-warning-600",
    icon: "text-warning-600",
    inverse: "text-warning-400",
  }),
  rose: ramp({
    border: "border-rose-200",
    bg: "bg-rose-50/60",
    text: "text-rose-700",
    solid: "bg-rose-500",
    tile: "bg-rose-50 text-rose-600",
    icon: "text-rose-600",
  }),
  teal: ramp({
    border: "border-teal-200",
    bg: "bg-teal-50/60",
    text: "text-teal-700",
    solid: "bg-teal-500",
    tile: "bg-teal-50 text-teal-600",
    icon: "text-teal-600",
  }),
  indigo: ramp({
    border: "border-indigo-200",
    bg: "bg-indigo-50/60",
    text: "text-indigo-700",
    solid: "bg-indigo-500",
    tile: "bg-indigo-50 text-indigo-600",
    icon: "text-indigo-600",
  }),
  violet: ramp({
    border: "border-violet-200",
    bg: "bg-violet-50/60",
    text: "text-violet-700",
    solid: "bg-violet-500",
    tile: "bg-violet-50 text-violet-600",
    icon: "text-violet-600",
  }),
  orange: ramp({
    border: "border-orange-200",
    bg: "bg-orange-50/60",
    text: "text-orange-700",
    solid: "bg-orange-500",
    tile: "bg-orange-50 text-orange-600",
    icon: "text-orange-600",
  }),
  pink: ramp({
    border: "border-pink-200",
    bg: "bg-pink-50/60",
    text: "text-pink-700",
    solid: "bg-pink-500",
    tile: "bg-pink-50 text-pink-600",
    icon: "text-pink-600",
  }),
  cyan: ramp({
    border: "border-cyan-200",
    bg: "bg-cyan-50/60",
    text: "text-cyan-700",
    solid: "bg-cyan-500",
    tile: "bg-cyan-50 text-cyan-600",
    icon: "text-cyan-600",
  }),
  lime: ramp({
    border: "border-lime-200",
    bg: "bg-lime-50/60",
    text: "text-lime-700",
    solid: "bg-lime-500",
    tile: "bg-lime-50 text-lime-600",
    icon: "text-lime-600",
  }),
  fuchsia: ramp({
    border: "border-fuchsia-200",
    bg: "bg-fuchsia-50/60",
    text: "text-fuchsia-700",
    solid: "bg-fuchsia-500",
    tile: "bg-fuchsia-50 text-fuchsia-600",
    icon: "text-fuchsia-600",
  }),
  sky: ramp({
    border: "border-sky-200",
    bg: "bg-sky-50/60",
    text: "text-sky-700",
    solid: "bg-sky-500",
    tile: "bg-sky-50 text-sky-600",
    icon: "text-sky-600",
  }),
  yellow: ramp({
    border: "border-yellow-200",
    bg: "bg-yellow-50/60",
    text: "text-yellow-700",
    solid: "bg-yellow-500",
    tile: "bg-yellow-50 text-yellow-600",
    icon: "text-yellow-600",
  }),
  slate: ramp({
    border: "border-slate-200",
    bg: "bg-slate-50/60",
    text: "text-slate-700",
    solid: "bg-slate-500",
    tile: "bg-slate-50 text-slate-600",
    icon: "text-slate-600",
  }),
  stone: ramp({
    border: "border-stone-200",
    bg: "bg-stone-50/60",
    text: "text-stone-700",
    solid: "bg-stone-500",
    tile: "bg-stone-50 text-stone-600",
    icon: "text-stone-600",
  }),
};

/**
 * A hue's ramp, falling back to neutral for an unknown or missing name.
 * `Object.hasOwn` rather than a plain lookup so a tone named "constructor" or
 * "toString" resolves to neutral instead of an inherited Object member.
 */
export function getTone(hue) {
  return Object.hasOwn(TONES, hue) ? TONES[hue] : TONES.gray;
}

/**
 * A hue as text on the espresso surface: its `inverse` slot, or neutral's for
 * a hue that has none.
 */
export function getInverseText(hue) {
  return getTone(hue).inverse || TONES.gray.inverse;
}

/**
 * The score bands (docs/design-guidelines.md §2). One definition for every
 * surface that colors a percentage — the recent-result card, the dashboard top
 * score, the transcript table — so a band never means 80 here and 75 there.
 */
export const SCORE_BAND_MIN = { good: 80, fair: 65 };

/** Hue name for a 0–100 score. */
export function getScoreToneName(score) {
  if (score >= SCORE_BAND_MIN.good) return "emerald";
  if (score >= SCORE_BAND_MIN.fair) return "warning";
  return "rose";
}

/** Full ramp for a 0–100 score: `{ border, bg, text, dot, pill, tile, icon }`. */
export function getScoreTone(score) {
  return TONES[getScoreToneName(score)];
}
